import {
  Button, Typography, useTheme, Chip, Box,
  List, ListItemButton, ListItemText, Grid, Paper, Divider,
  Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, IconButton, Tooltip, Stack, CircularProgress,
  Collapse,
} from "@mui/material";
import CustomCard from "../../shared/components/Card";
import { Fragment } from "react";
import ComboBox from "../../shared/components/ComboBox";
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import TuneIcon from '@mui/icons-material/Tune';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchInput from "../../shared/components/SearchInput";
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useTracing, statusColor, statusCardColor, type DataRow, type DataRowDetail } from "./hooks";
import GenericDialog from "../../shared/components/GenericDialog";
import PaginatedTable, { type PaginatedTableColumn } from "../../shared/components/PaginatedTable";

const LOG_COLUMNS: PaginatedTableColumn[] = [
  { key: 'date', label: 'Fecha', sx: { width: '9rem', whiteSpace: 'nowrap' } },
  { key: 'result', label: 'Resultado', sx: { width: '7rem', whiteSpace: 'nowrap' } },
  { key: 'message', label: 'Mensaje' },
];

const Tracing: React.FC = () => {
  const {
    statuses,
    tableData,
    selectedClient,
    selectedClientId,
    serviceOptions,
    statusOptions,
    selectedService,
    selectedStatus,
    searchText,
    isLogOpen,
    selectedLogService,
    logDetails,
    logLoading,
    logError,
    handleStatusClick,
    handleStatusChange,
    handleSearchChange,
    handleServiceChange,
    handleSelectClient,
    handleClearFilters,
    handleOpenLog,
    handleCloseLog,
    filtersOpen,
    setFiltersOpen,
    mobileExpandedId,
    handleMobileToggle,
  } = useTracing();

  const theme = useTheme();

  return (
    <Fragment>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: { lg: '100%' }, overflow: { lg: 'hidden' } }}>

        {/* ── STATUS BAR ── */}

        {/* Mobile: chips con scroll horizontal */}
        <Box sx={{
          display: { xs: 'flex', lg: 'none' },
          overflowX: 'auto',
          gap: 1,
          px: 1.5,
          py: 1,
          flexShrink: 0,
          backgroundColor: theme.palette.background.main,
          '&::-webkit-scrollbar': { display: 'none' },
        }}>
          {statuses?.map(status => (
            <Chip
              key={`status-chip-${status.result}`}
              label={`${status.quantity} ${status.result}`}
              color={statusCardColor(status.result)}
              variant={selectedStatus === status.result ? 'filled' : 'outlined'}
              onClick={() => handleStatusClick(status.result)}
              clickable
              sx={{ flexShrink: 0 }}
            />
          ))}
        </Box>

        {/* Desktop: cards */}
        <Box sx={{
          display: { xs: 'none', lg: 'flex' },
          flexWrap: 'wrap',
          alignItems: 'center',
          px: 2,
          backgroundColor: theme.palette.background.main,
        }}>
          {statuses?.map(status => (
            <Tooltip
              key={`status-tooltip-${status.result}`}
              title={status.description ?? ''}
              arrow
              disableHoverListener={!status.description}
            >
              <span>
                <CustomCard
                  title={`${status.quantity}`}
                  content={`Servicios ${status.result}`}
                  color={statusCardColor(status.result)}
                  onClick={() => handleStatusClick(status.result)}
                />
              </span>
            </Tooltip>
          ))}
        </Box>

        {/* ── FILTER BAR ── */}

        {/* Mobile: search + toggle + limpiar */}
        <Box sx={{
          display: { xs: 'flex', lg: 'none' },
          alignItems: 'center',
          px: 1.5,
          pt: 1,
          pb: 0.5,
          gap: 1,
          backgroundColor: theme.palette.background.main,
        }}>
          <SearchInput
            label="Buscar"
            placeholder="Empresa, Cliente"
            value={searchText}
            onChange={handleSearchChange}
            variant="standard"
            sx={{ flex: 1 }}
          />
          <Tooltip title="Filtros">
            <IconButton
              size="small"
              onClick={() => setFiltersOpen(p => !p)}
              color={filtersOpen ? 'primary' : 'default'}
              sx={{ '&:focus': { outline: 'none', boxShadow: 'none' } }}
            >
              <TuneIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Limpiar filtros">
            <IconButton
              size="small"
              onClick={handleClearFilters}
              sx={{ '&:focus': { outline: 'none', boxShadow: 'none' } }}
            >
              <FilterAltOffIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Mobile: ComboBoxes colapsables */}
        <Collapse in={filtersOpen} sx={{ display: { xs: 'block', lg: 'none' }, backgroundColor: theme.palette.background.main }}>
          <Stack sx={{ px: 1.5, pb: 1.5 }} spacing={1}>
            <ComboBox
              options={serviceOptions}
              title="Servicios"
              value={selectedService}
              onChange={handleServiceChange}
              sx={{ width: '100%', ml: 0, mb: 0 }}
            />
            <ComboBox
              options={statusOptions}
              title="Estados"
              value={selectedStatus ?? 'Todos'}
              onChange={handleStatusChange}
              sx={{ width: '100%', ml: 0, mb: 0 }}
            />
          </Stack>
        </Collapse>

        {/* Desktop: filter bar horizontal */}
        <Box sx={{
          display: { xs: 'none', lg: 'flex' },
          alignItems: 'center',
          flexWrap: 'wrap',
          px: 2,
          gap: 0,
          backgroundColor: theme.palette.background.main,
        }}>
          <SearchInput
            label="Buscar"
            placeholder="Empresa, Cliente"
            value={searchText}
            onChange={handleSearchChange}
            variant="standard"
            sx={{ ml: 1.25, minWidth: '18.75rem', mb: 2 }}
          />
          <ComboBox
            options={serviceOptions}
            title={'Servicios'}
            value={selectedService}
            onChange={handleServiceChange}
          />
          <ComboBox
            options={statusOptions}
            title={'Estados'}
            value={selectedStatus ?? 'Todos'}
            onChange={handleStatusChange}
          />
          <Tooltip title="Limpiar filtros">
            <IconButton
              onClick={handleClearFilters}
              size="small"
              sx={{ ml: 1, '&:focus': { outline: 'none', boxShadow: 'none' } }}
            >
              <FilterAltOffIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* ── CONTENIDO PRINCIPAL ── */}

        {/* Mobile: lista acordeón */}
        <Box sx={{
          display: { xs: 'block', lg: 'none' },
          overflow: 'auto',
          flex: 1,
          pt: 1,
        }}>
          <Paper elevation={1} sx={{ mx: 1, mb: 2 }}>
            <List disablePadding>
              {tableData.map((client: DataRow) => (
                <Fragment key={client.id}>
                  <ListItemButton
                    onClick={() => handleMobileToggle(client.id)}
                    sx={{ '&:focus': { outline: 'none', boxShadow: 'none' } }}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight={600}>
                          {client.customer}
                        </Typography>
                      }
                      secondary={client.business}
                    />
                    <Chip
                      label={client.status}
                      size="small"
                      color={statusColor(client.status)}
                      sx={{ mr: 1, flexShrink: 0 }}
                    />
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        flexShrink: 0,
                        transition: 'transform 0.2s',
                        transform: mobileExpandedId === client.id ? 'rotate(180deg)' : 'none',
                      }}
                    />
                  </ListItemButton>

                  <Collapse in={mobileExpandedId === client.id} timeout="auto" unmountOnExit>
                    {client.details.length === 0 ? (
                      <Box sx={{ px: 2, py: 1.5, bgcolor: 'grey.50' }}>
                        <Typography variant="caption" color="text.secondary">
                          Sin servicios registrados.
                        </Typography>
                      </Box>
                    ) : (
                      client.details.map((service: DataRowDetail) => (
                        <Box
                          key={service.id}
                          sx={{
                            px: 2,
                            py: 1.25,
                            bgcolor: 'grey.50',
                            borderTop: '1px solid',
                            borderColor: 'divider',
                            opacity: service.inactive ? 0.6 : 1,
                          }}
                        >
                          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Stack direction="row" spacing={1} alignItems="center" mb={0.5} flexWrap="wrap">
                                <Typography variant="body2" fontWeight={500} noWrap>
                                  {service.service}
                                </Typography>
                                {service.inactive && (
                                  <Chip label="Inactivo" size="small" variant="outlined" />
                                )}
                              </Stack>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Chip
                                  label={service.status}
                                  size="small"
                                  color={statusColor(service.status)}
                                />
                                <Typography variant="caption" color="text.secondary">
                                  {service.lastRun}
                                </Typography>
                              </Stack>
                            </Box>
                            <Tooltip title="Ver log">
                              <IconButton
                                size="small"
                                color="info"
                                onClick={(e) => { e.stopPropagation(); handleOpenLog(service); }}
                                sx={{ flexShrink: 0, '&:focus': { outline: 'none', boxShadow: 'none' } }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </Box>
                      ))
                    )}
                  </Collapse>
                  <Divider />
                </Fragment>
              ))}
            </List>
          </Paper>
        </Box>

        {/* Desktop: master-detail */}
        <Box sx={{ display: { xs: 'none', lg: 'flex' }, flexDirection: 'column', px: 2, pb: 2, flex: 1, overflow: 'hidden' }}>
          <Grid container spacing={2} alignItems="flex-start" sx={{ height: '100%' }}>

            {/* ── MASTER: Sidebar de clientes ── */}
            <Grid size={{ xs: 12, lg: 3 }} sx={{ height: '100%' }}>
              <Paper elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Clientes
                  </Typography>
                </Box>
                <List disablePadding sx={{ overflow: 'auto', flex: 1 }}>
                  {tableData.map((client: DataRow) => (
                    <Fragment key={client.id}>
                      <ListItemButton
                        selected={selectedClientId === client.id}
                        onClick={() => handleSelectClient(client.id)}
                        sx={{
                          '&.Mui-selected': {
                            backgroundColor: theme.palette[statusColor(client.status)].main,
                            '&:hover': { backgroundColor: theme.palette[statusColor(client.status)].main },
                            '& .MuiTypography-root': { color: 'white' },
                          },
                        }}
                      >
                        <ListItemText
                          primary={
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {client.customer}
                            </Typography>
                          }
                          secondary={
                            <Stack direction="row" alignItems="center" spacing={1} mt={0.5}>
                              <Typography variant="caption" sx={{ color: selectedClientId === client.id ? 'white' : 'text.secondary' }}>
                                {client.business}
                              </Typography>
                              <Chip
                                label={client.status}
                                size="small"
                                color={statusColor(client.status)}
                                variant={selectedClientId === client.id ? 'outlined' : 'filled'}
                                sx={selectedClientId === client.id ? { borderColor: 'white', color: 'white' } : {}}
                              />
                            </Stack>
                          }
                        />
                      </ListItemButton>
                      <Divider />
                    </Fragment>
                  ))}
                </List>
              </Paper>
            </Grid>

            {/* ── DETAIL: Workspace del cliente ── */}
            <Grid size={{ xs: 12, lg: 9 }}>
              {selectedClient ? (
                <Stack spacing={2}>
                  <Paper elevation={2} sx={{ px: 3, py: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {selectedClient.customer}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedClient.business}
                      </Typography>
                      <Chip
                        label={selectedClient.status}
                        size="small"
                        color={statusColor(selectedClient.status)}
                      />
                    </Box>
                  </Paper>

                  <Paper elevation={2} sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Servicios ({selectedClient.details.length})
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    {selectedClient.details.length > 0 ? (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ backgroundColor: theme.palette.background.main }}>
                              <TableCell sx={{ fontWeight: 600 }}>Servicio</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Última ejecución</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Frecuencia</TableCell>
                              <TableCell sx={{ fontWeight: 600 }} align="center">Acciones</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selectedClient.details.map((service: DataRowDetail) => (
                              <TableRow
                                key={service.id}
                                hover
                                sx={{
                                  '&:last-child td': { borderBottom: 0 },
                                  ...(service.inactive && {
                                    backgroundColor: theme.palette.action.hover,
                                    opacity: 0.6,
                                  }),
                                }}
                              >
                                <TableCell>
                                  <Stack direction="row" alignItems="center" spacing={1}>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {service.service}
                                    </Typography>
                                    {service.inactive && (
                                      <Chip label="Inactivo" size="small" variant="outlined" />
                                    )}
                                  </Stack>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={service.status}
                                    size="small"
                                    color={statusColor(service.status)}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary">
                                    {service.lastRun}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary">
                                    {service.frequency}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Tooltip title="Ver detalle">
                                    <IconButton
                                      size="small"
                                      color="info"
                                      onClick={() => handleOpenLog(service)}
                                      sx={{ '&:focus': { outline: 'none', boxShadow: 'none' } }}
                                    >
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body1" color="text.secondary">
                          No hay servicios registrados para este cliente
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </Stack>
              ) : (
                <Paper elevation={2}>
                  <Box sx={{ textAlign: 'center', py: 10 }}>
                    <Typography variant="h6" color="text.secondary">
                      Seleccioná un cliente para ver sus servicios
                    </Typography>
                  </Box>
                </Paper>
              )}
            </Grid>

          </Grid>
        </Box>
      </Box>

      {/* Dialog: Log */}
      <GenericDialog
        open={isLogOpen}
        onClose={handleCloseLog}
        title={`Log${selectedClient ? ` - ${selectedClient.customer}` : ''}${selectedLogService ? ` - ${selectedLogService.service}` : ''}`}
        maxWidth="md"
        fullScreen={false}
        sx={{
          '& .MuiDialog-paper': {
            width: { xs: '95%', md: '80%' },
            maxWidth: { xs: '95%', md: '80%' },
            maxHeight: '85vh',
          },
        }}
        actions={
          <Button
            onClick={handleCloseLog}
            size="small"
            variant="outlined"
            sx={{ '&:focus': { outline: 'none', boxShadow: 'none' } }}
          >
            Cerrar
          </Button>
        }
      >
        {logLoading ? (
          <Stack direction="row" alignItems="center" spacing={1}>
            <CircularProgress size={18} />
            <Typography variant="body2">Cargando log...</Typography>
          </Stack>
        ) : logError ? (
          <Typography variant="body2" color="error">
            No se pudo cargar el log.
          </Typography>
        ) : (
          <PaginatedTable
            columns={LOG_COLUMNS}
            rows={logDetails}
            rowsPerPage={8}
            emptyMessage="Sin registros de ejecución."
            headerSx={{ backgroundColor: theme.palette.background.main }}
            renderRow={(detail) => (
              <TableRow key={detail.id} hover>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {new Date(detail.date).toLocaleString('es-AR', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: true,
                    })}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={detail.result?.trim() || 'Sin resultado'}
                    size="small"
                    color={statusColor(detail.result ?? '')}
                    sx={{ fontWeight: 600 }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {detail.message}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          />
        )}
      </GenericDialog>
    </Fragment>
  );
};

export default Tracing;
